/**
 * SATCHEL — keep the product sticky bar visible all the way down the page.
 *
 * Prestige hides the sticky bar as soon as the footer scrolls into view. Its own
 * observer ANDs two conditions together:
 *
 *   isVisible = (buy form has scrolled above the viewport) && (footer is NOT visible)
 *
 * We cannot drop the footer half of that test without editing theme.js, which a theme
 * update would overwrite. So this script leaves Prestige's `is-visible` class alone and
 * maintains a second class of its own, driven only by the form half of the test. A CSS
 * rule on that class wins on specificity, so the bar stays up once the buy form is past,
 * footer or not.
 *
 * It also publishes the bar's height as --satchel-sticky-bar-height, so the footer can
 * reserve room and its last row is not permanently sitting under the bar.
 */
(() => {
  const VISIBLE_CLASS = 'satchel-sticky-bar--visible';
  const HEIGHT_PROPERTY = '--satchel-sticky-bar-height';

  /**
   * Mirrors Prestige's own test for "the buy form has scrolled above the viewport".
   * Keeping the same test means the bar appears at exactly the same scroll position it
   * always did; the only behaviour that changes is that it no longer disappears.
   */
  const hasScrolledPast = (entry) => !entry.isIntersecting && entry.boundingClientRect.bottom < 0;

  const track = (bar) => {
    if (bar.dataset.satchelTracked === 'true') return;

    const form = document.forms[bar.getAttribute('form')];
    if (!form) return;

    bar.dataset.satchelTracked = 'true';

    const visibility = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        bar.classList.toggle(VISIBLE_CLASS, hasScrolledPast(entry));
      }
    });

    visibility.observe(form);

    // The bar's height changes with the viewport and with its own content, so measure it
    // rather than hardcoding a value the next design change would silently invalidate.
    const publishHeight = () => {
      document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${bar.offsetHeight}px`);
    };

    if ('ResizeObserver' in window) {
      new ResizeObserver(publishHeight).observe(bar);
    }

    publishHeight();
  };

  const init = () => {
    document.querySelectorAll('product-sticky-bar').forEach(track);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The bar sits inside a <product-rerender>, which replaces its own contents when the
  // selected variant changes. Watch just that container, not the whole document, so a
  // rerender re-attaches the observer without the cost of a document-wide subtree watch.
  document.querySelectorAll('product-rerender[id$="-sticky-bar"]').forEach((container) => {
    new MutationObserver(init).observe(container, { childList: true });
  });
})();
